<template>
  <div class="ndvrPlayerControls">
    <el-row type="flex" align="middle" class="wrapper-box">
      <el-col :span="2" :offset="3">
        <el-button
          :disabled="!isSnapshotEnabled"
          :title="isSnapshotEnabled ? 'Make Snapshot (S)' : 'Snapshot Disabled'"
          type="plain"
          size="mini"
          class="player-control-btn"
          @click="makeSnapshot"
        >
          <camera
            :title="
              isSnapshotEnabled ? 'Make Snapshot (S)' : 'Snapshot Disabled'
            "
            class="player-control-icon"
          />
        </el-button>
      </el-col>
      <!-- volume button -->
      <el-col
        class="volume-row"
        :xl="{ span: 4, offset: 6 }"
        :lg="{ span: 4, offset: 5 }"
        :md="{ span: 4, offset: 6 }"
        :sm="{ span: 5, offset: 5 }"
        :xs="{ span: 7, offset: 3 }"
      >
        <el-button
          :title="toggleMuteLabel"
          type="plain"
          size="mini"
          class="muteButton player-control-btn"
          @click="toggleMutePlayer"
        >
          <volume-high
            v-if="!isMuted && isHigh"
            class="player-control-icon"
            :title="toggleMuteLabel"
          />
          <volume-medium
            v-if="!isMuted && isMedium"
            class="player-control-icon"
            :title="toggleMuteLabel"
          />
          <volume-low
            v-if="!isMuted && isLow"
            class="player-control-icon"
            :title="toggleMuteLabel"
          />
          <volume-off
            v-if="isMuted"
            class="player-control-icon"
            :title="toggleMuteLabel"
          />
        </el-button>

        <!-- Volume slider -->
        <vue-slider
          v-if="showVolumeSlider"
          class="volume-row__slider"
          :value="volume * 100"
          :tooltip-formatter="volumeTooltipFormatter"
          :use-keyboard="false"
          :dot-style="soundSliderDotStyle"
          @change="changeVolume($event / 100)"
        />
      </el-col>
    </el-row>
  </div>
</template>

<script>
/** time icon */
/** volume icons */
import VolumeHigh from "vue-material-design-icons/VolumeHigh.vue";
import VolumeLow from "vue-material-design-icons/VolumeLow.vue";
import VolumeMedium from "vue-material-design-icons/VolumeMedium.vue";
import VolumeOff from "vue-material-design-icons/VolumeOff.vue";
/** snapshot icon */
import Camera from "vue-material-design-icons/Camera.vue";
/** volume slider */
import VueSlider from "vue-slider-component";

/** shortcuts tools */
import { subscribeToKeydown, subscribeToKeyup } from "@/utils/shortcuts.js";

/** device checking methods */
import { isApplePhone } from "@/utils/device.js";

/** time formatting */

// @group Media/Controls
// Player controls bar
export default {
  name: "PlayerControls",
  components: {
    /** time icon */
    /** audio icons*/
    VolumeHigh,
    VolumeMedium,
    VolumeLow,
    VolumeOff,
    VueSlider,
    /** snapshot icon */
    Camera
  },
  props: {
    // Sound volume level
    volume: {
      type: Number,
      default: 1.0,
      validator(val) {
        return val >= 0 && val <= 1;
      }
    },
    // Is sound muted
    muted: {
      type: Boolean,
      default: false
    },
    // Is snapshot button available
    isSnapshotEnabled: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      volumeTooltipFormatter: v => `${Math.round(v)}%`,
      volumeStep: 0.05,
      volumeChangeInterval: 100,
      increaseVolumeHold: false,
      decreaseVolumeHold: false,
      soundSliderDotStyle: {
        margin: "2px 0 0 5px",
        width: "12px",
        height: "12px"
      },
      /** show volume slider flag - may be false on iOS */
      showVolumeSlider: true
    };
  },
  computed: {
    // @vuese
    // Is player muted
    isMuted() {
      return this.muted == true || this.volume == 0.0;
    },
    // @vuese
    // Is player loud
    isHigh() {
      return this.volume > 0.67;
    },
    // @vuese
    // Is player at medium volume
    isMedium() {
      return this.volume > 0.34 && this.volume <= 0.67;
    },
    // @vuese
    // Is player quiet
    isLow() {
      return this.volume <= 0.34;
    },
    // @vuese
    // Label for toggle mute button
    toggleMuteLabel() {
      return this.isMuted ? "Unmute" : "Mute";
    }
  },
  mounted() {
    /**
     * disable volume slider on iOS
     * because it doesn't support
     * volume changing
     */
    this.showVolumeSlider = !isApplePhone();

    /** volume shortcuts */
    /** start volume increasing */
    subscribeToKeydown("ArrowUp", () => {
      if (!this.increaseVolumeHold) {
        this.increaseVolumeHold = true;
        this.incVolume();
      }
    });

    /** stop volume increasing */
    subscribeToKeyup("ArrowUp", () => {
      this.increaseVolumeHold = false;
    });

    /** start volume decreasing */
    subscribeToKeydown("ArrowDown", () => {
      if (!this.decreaseVolumeHold) {
        this.decreaseVolumeHold = true;
        this.decVolume();
      }
    });

    /** stop volume decreasing */
    subscribeToKeyup("ArrowDown", () => {
      this.decreaseVolumeHold = false;
    });
  },
  methods: {
    // @vuese
    // Set sound volume
    // @arg vol - new volume in percents
    changeVolume(vol) {
      this.$emit("changeVolume", vol);
    },
    // @vuese
    // Get snapshot
    makeSnapshot() {
      this.$emit("snapshot");
    },
    // @vuese
    // Toggle mute
    toggleMutePlayer() {
      this.$emit("mute");
    },
    // @vuese
    // Increase volume recursively while increasing hold flag is set
    incVolume() {
      if (this.increaseVolumeHold) {
        this.changeVolume(this.volume + this.volumeStep);
        setTimeout(() => this.incVolume(), this.volumeChangeInterval);
      }
    },
    // @vuese
    // Decrease volume recursively while decreasing hold flag is set
    decVolume() {
      if (this.decreaseVolumeHold) {
        this.changeVolume(this.volume - this.volumeStep);
        setTimeout(() => this.decVolume(), this.volumeChangeInterval);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.muteButton{
  background: #ed5229;
}

.ndvrPlayerControls {
  background: #233d4e;
  margin-left: auto;
  margin-right: auto;
  position: absolute;
  width: 100%;
  bottom: 0;
  padding: 0.75em 0;
}
.time-block {
  display: flex;
  align-items: center;
}
.time-label {
  font-weight: bold;
  font-size: 1.2rem;
  margin-left: 5px;
}

.volume-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  &__slider {
    flex-grow: 1;
    margin-left: 10px;
  }
}

.el-col-offset-3 {
  margin-left: 6.5%;
}

.wrapper-box {
  display: flex;
  justify-content: space-around;
}


@media only screen and (max-width: 767px) {
  .player-control-btn {
    width: 2.2rem;
    height: 2.2rem;
  }
  .player-control-icon .material-design-icon__svg {
    width: 1.6rem !important;
    height: 1.6rem !important;
  }
}

@media only screen and (min-width: 768px) {
  .player-control-btn {
    width: 2.2rem;
    height: 2.2rem;
  }
  .player-control-icon .material-design-icon__svg {
    width: 1.6rem !important;
    height: 1.6rem !important;
  }
}
@media only screen and (min-width: 992px) {
  .player-control-btn {
    width: 1.8rem;
    height: 1.8rem;
  }
  .player-control-icon .material-design-icon__svg {
    width: 1.2rem !important;
    height: 1.2rem !important;
  }
}
@media only screen and (min-width: 1200px) {
  .player-control-btn {
    width: 2.4rem;
    height: 2.4rem;
  }
  .player-control-icon .material-design-icon__svg {
    width: 1.5rem !important;
    height: 1.5rem !important;
  }
}
@media only screen and (min-width: 1920px) {
  .time-label {
    font-size: 1.4rem;
  }
  .player-control-btn {
    width: 2.5rem;
    height: 2.5rem;
  }
  .player-control-icon .material-design-icon__svg {
    width: 1.5rem !important;
    height: 1.5rem !important;
  }
}
</style>
